import React, { useCallback, useContext, useMemo } from "react";
import { Form, Formik } from "formik";
import FormikTextInput from "../../../formikComponents/formik-text-input";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import { Close, Edit, Save } from "@mui/icons-material";
import {
  arrayRemove,
  arrayUnion,
  doc,
  getDoc,
  updateDoc,
} from "@firebase/firestore";
import { customFirestore } from "../../../firebase";
import { auditMeta } from "../../../utils/auditMeta";
import { type EnvVar } from "../../../utils/types";
import { companyDataAtom } from "../../test-run/test-run-atoms";
import { trackSentryException } from "../../../utils/helpers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { atomWithReset } from "jotai/utils";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { getTestsAtom } from "../../../atoms/testing-settings-atoms";
import { AuthContext } from "../../../auth/AuthContext";
import { customAlertStateAtom } from "../../../components/custom-alert";

const EditEnvVarDialog = () => {
  const queryClient = useQueryClient();
  const { user } = useContext<any>(AuthContext);

  const [dialogState, setDialogState] = useAtom(editEnvVarDialogStateAtom);
  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const handleClose = useCallback((event: {}, reason: string) => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown"))
      return;
    setDialogState({ open: false, envVarKey: undefined });
  }, []);

  const { data: tests } = useAtomValue(
    getTestsAtom({ organizationId: user.companyId })
  );

  const affectedTests = useMemo(
    () =>
      tests?.values
        .filter(
          (test) =>
            dialogState.envVarKey != null &&
            test.commands.includes(`{{env.${dialogState.envVarKey}}}`)
        )
        .map((test) => test.title) ?? [],
    [dialogState, tests]
  );

  const editEnvVarMutation = useMutation({
    mutationKey: ["editEnvVarDialog"],
    mutationFn: async ({
      envVarKey,
      newValue,
    }: {
      envVarKey: string;
      newValue: string;
    }) => {
      try {
        const settingsDoc = doc(customFirestore, "settings", user.companyId);
        const envVars: EnvVar[] =
          (await getDoc(settingsDoc)).data()?.envVars ?? [];
        const foundEnvVar = envVars.find((envVar) => envVar.key === envVarKey);

        if (foundEnvVar) {
          const updatedEnvVar: EnvVar = {
            key: envVarKey,
            value: newValue,
            lastEditTimestamp: Math.floor(Date.now() / 1000),
            isSecret: foundEnvVar.isSecret ?? false,
          };
          await updateDoc(settingsDoc, { envVars: arrayRemove(foundEnvVar) });
          await updateDoc(settingsDoc, {
            envVars: arrayUnion(updatedEnvVar),
            ...auditMeta(),
          });
          await queryClient.invalidateQueries({
            queryKey: ["getEnvVars", user.companyId],
          });
          companyDataAtom.remove({ organisationId: user.companyId });
          queryClient.removeQueries({
            queryKey: ["getCompanyData"],
          });
          setCustomAlertState({
            open: true,
            type: "success",
            title: `Env var "${envVarKey}" updated successfully`,
          });
        } else {
          setCustomAlertState({
            open: true,
            type: "error",
            title: "Something went wrong",
            description: `Env Var with key ${deleteEnvVarDialogState!} not found`,
          });
        }
      } catch (error: any) {
        trackSentryException(error);
        console.error("An error occurred during editing", error);
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: "An error occurred during editing",
        });
      }
      handleClose({}, "closeIconButtonClose");
    },
  });

  return (
    <Dialog
      maxWidth={"xs"}
      fullWidth={true}
      open={dialogState.open}
      onClose={handleClose}
    >
      <Formik
        initialValues={{ envVarValue: "" }}
        validate={(values) => {
          const errors: { envVarValue?: string } = {};
          if (!values.envVarValue) {
            errors.envVarValue = "Required";
          }
          return errors;
        }}
        onSubmit={async (values) => {
          if (affectedTests.length > 0) {
            let message = `Editing this Env Var will affect ${affectedTests.length} test(s): \n`;
            for (const title of affectedTests) {
              message += title + "\n";
            }
            console.log("affected tests", affectedTests);
            if (window.confirm(message)) {
              await editEnvVarMutation.mutateAsync({
                envVarKey: dialogState.envVarKey!,
                newValue: values.envVarValue,
              });
            }
          } else {
            await editEnvVarMutation.mutateAsync({
              envVarKey: dialogState.envVarKey!,
              newValue: values.envVarValue,
            });
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogTitle
              id="alert-dialog-title"
              sx={{ paddingRight: "8px", paddingTop: "10px" }}
            >
              <Box display="flex" alignItems="top">
                <Box flexGrow={1} paddingRight={"6px"} paddingTop={"4px"}>
                  Enter new value
                </Box>
                <Box>
                  <Tooltip title={"Close"}>
                    <span>
                      <IconButton
                        disabled={editEnvVarMutation.isPending}
                        onClick={() => {
                          handleClose({}, "closeIconButtonClose");
                        }}
                      >
                        <Close />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers className={"flex flex-col gap-3.5"}>
              <FormikTextInput
                name={`envVarValue`}
                label={"Value"}
                placeholder={"Value"}
                type={"text"}
                required
              />
            </DialogContent>
            <DialogActions>
              <Button
                loading={isSubmitting}
                type={"submit"}
                loadingPosition="start"
                startIcon={<Save />}
                variant="contained"
              >
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

const editEnvVarDialogStateAtom = atomWithReset<{
  envVarKey?: string;
  open: boolean;
}>({ open: false });

export default EditEnvVarDialog;

export { editEnvVarDialogStateAtom };
